import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import styled from "styled-components";

import { Icon } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import { useGetDrugQuery } from "@/App/api/drug";
import { useGetPharmacyQuery } from "@/App/api/pharmacy";
import { ContentPanel, DrugDetails, PageWrapper, PaymentDetails, PharmacyDetails } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

import { DrugHeader } from "./DrugHeader";

const LargeIcon = styled(Icon)`
  min-height: unset !important;
  max-height: unset !important;
  min-width: unset !important;
  max-width: unset !important;
  width: 32px;
  height: 32px;
`;

export const FindBestPricePricingInfoPage: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const [searchParams] = useSearchParams();
  const { drug_id: drugId } = useParams();

  const { columns } = useResponsive({
    columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
  });

  const dosageForm = searchParams.get("dosage_form");
  const dosage = searchParams.get("dosage");
  const quantity = searchParams.get("quantity");
  const daysSupply = searchParams.get("days_supply");
  const location: { readonly latitude: string; readonly longitude: string } = JSON.parse(
    decodeURIComponent(searchParams.get("loc") || "{}"),
  );
  const providerId = searchParams.get("provider_id");

  const patientPayAmount = decodeURIComponent(searchParams.get("patient_pay") || "");
  const planPayAmount = decodeURIComponent(searchParams.get("plan_pay") || "");
  const rejectMessage = decodeURIComponent(searchParams.get("reject_message") || "");

  const { data: drug, isLoading: isLoadingDrug } = useGetDrugQuery(+drugId);
  const { data: pharmacy, isLoading: isLoadingPharmacy } = useGetPharmacyQuery(providerId);

  const onBackPressed = useCallback(() => {
    const infoParams = new URLSearchParams();
    infoParams.set("dosage_form", dosageForm);
    infoParams.set("dosage", dosage);
    infoParams.set("quantity", quantity);
    infoParams.set("days_supply", daysSupply);
    infoParams.set("loc", searchParams.get("loc"));
    const submissionClarificationCode = searchParams.get("submission_clarification_code");
    const hasShownCopayMaxMessage = searchParams.get("copayMaxModalShown");
    if (submissionClarificationCode != null) {
      infoParams.set("submission_clarification_code", submissionClarificationCode);
    }
    if (hasShownCopayMaxMessage != null) {
      infoParams.set("copayMaxModalShown", hasShownCopayMaxMessage);
    }

    logEvent(ANALYTIC_EVENTS.PRESCRIPTION.backButton);

    navigate({
      pathname: `/find-best-price/compare-prices/${drugId}`,
      search: infoParams.toString(),
    });
  }, [dosageForm, dosage, quantity, daysSupply, location]);

  return (
    <PageWrapper>
      <ContentPanel title={<DrugHeader onBackPressed={onBackPressed} drug={drug?.drug} isLoading={isLoadingDrug} hideDrugDetails />}>
        <Grid>
          {rejectMessage.length > 0 && (
            <Grid.Span columns={12}>
              <Shelf alignment="center" testId="rejection-message-container">
                <Stack>
                  <Shelf>
                    <Shelf.Spacer />
                    <LargeIcon name="x-circle" color="red" />
                    <Shelf.Spacer />
                  </Shelf>
                  <Subheading textAlign="center">{translate("FIND_BEST_PRICE.findBestPricePricingInfoPage.pharmacyHasRejected")}</Subheading>
                  <Paragraph textAlign="center" testId="rejection-messages">{translate("FIND_BEST_PRICE.findBestPricePricingInfoPage.reasonForRejection")}{rejectMessage}</Paragraph>
                </Stack>
              </Shelf>
            </Grid.Span>
          )}
          {rejectMessage.length === 0 && (
            <Grid.Span columns={columns}>
              <Stack spacing="large">
                <PaymentDetails planPayAmount={+planPayAmount} patientPayAmount={+patientPayAmount} />
                <DrugDetails
                  quantity={quantity}
                  drugName={drug?.drug?.drug_name}
                  daysSupply={daysSupply}
                  dosage={dosage}
                  dosageForm={dosageForm}
                  drugType={drug?.drug?.drug_type}
                  isLoading={isLoadingDrug}
                />
              </Stack>
            </Grid.Span>
          )}
          {rejectMessage.length === 0 && (
            <Grid.Span columns={columns}>
              <PharmacyDetails {...{ ...pharmacy, isLoading: isLoadingPharmacy }} context="find_best_price" />
            </Grid.Span>
          )}
        </Grid>
      </ContentPanel>
    </PageWrapper>
  );
};
